import { useEffect, useRef, useState } from "react";
import { s } from "../style.js";
import { TASK_STATUSES } from "../data.js";
import { fetchRepos, fetchUsers } from "../api";
import type { TaskStatus, User, Repo } from "../types";

const kickerStyle = s(
  "font-size:14px; letter-spacing:0.14em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600)"
);

const fieldLabel = s(
  "font-size:12px; letter-spacing:0.12em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:6px"
);

const inputStyle = s(
  "width:100%; min-height:46px; padding:10px 14px; font:inherit; color:var(--color-text); background:var(--color-surface); border:2px solid var(--color-divider)"
);

export default function NewTicketSheet({ onClose, onCreate, defaultOwnerId = "" }: { onClose: () => void; onCreate: (input: unknown) => void; defaultOwnerId?: string }) {
  const [mounted, setMounted] = useState(false);
  const [title, setTitle] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [repos, setRepos] = useState<Repo[]>([]);
  const [ownerId, setOwnerId] = useState(defaultOwnerId || "");
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [selectedRepos, setSelectedRepos] = useState<string[]>([]);
  const [repoOpen, setRepoOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<TaskStatus>("Backlog");
  const [error, setError] = useState("");
  const closingRef = useRef(false);
  const ownerRef = useRef<HTMLDivElement | null>(null);
  const repoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    fetchUsers()
      .then((data) => setUsers(data))
      .catch(() => setUsers([]));
    fetchRepos()
      .then((data) => setRepos(data.filter((r: Repo) => r.active)))
      .catch(() => setRepos([]));
  }, []);

  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setMounted(false);
    setTimeout(onClose, 240);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (ownerOpen) {
        setOwnerOpen(false);
        return;
      }
      if (repoOpen) {
        setRepoOpen(false);
        return;
      }
      requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ownerOpen, repoOpen]);

  useEffect(() => {
    if (!ownerOpen && !repoOpen) return;
    const onPointer = (e: PointerEvent) => {
      if (ownerOpen && ownerRef.current && !ownerRef.current.contains(e.target as Node)) setOwnerOpen(false);
      if (repoOpen && repoRef.current && !repoRef.current.contains(e.target as Node)) setRepoOpen(false);
    };
    window.addEventListener("pointerdown", onPointer);
    return () => window.removeEventListener("pointerdown", onPointer);
  }, [ownerOpen, repoOpen]);

  const submit = () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!ownerId) {
      setError("Owner is required");
      return;
    }
    onCreate({
      title: title.trim(),
      ownerId,
      repos: selectedRepos,
      description: description.trim(),
      status,
    });
    requestClose();
  };

  return (
    <div style={s("position:absolute; inset:0; z-index:40")}>
      <div
        style={{
          ...s(
            "position:absolute; inset:0; background:color-mix(in srgb, var(--color-neutral-900) 50%, transparent)"
          ),
          opacity: mounted ? 1 : 0,
          transition: "opacity 240ms ease",
        }}
        onClick={requestClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        style={{
          ...s(
            "position:absolute; top:0; right:0; bottom:0; width:440px; background:var(--color-surface); color:var(--color-text); display:flex; flex-direction:column; border-left:2px solid var(--color-divider)"
          ),
          transform: mounted ? "translateX(0)" : "translateX(100%)",
          transition: "transform 240ms cubic-bezier(0.32,0.72,0,1)",
        }}
      >
        <div
          style={s(
            "flex:none; display:flex; align-items:center; gap:12px; padding:18px 22px; border-bottom:2px solid var(--color-divider)"
          )}
        >
          <span style={kickerStyle}>New ticket</span>
          <div style={s("flex:1")} />
          <button
            onClick={requestClose}
            aria-label="Close"
            style={s(
              "all:unset; cursor:pointer; width:44px; height:44px; display:flex; align-items:center; justify-content:center; font-size:30px; line-height:1; color:var(--color-neutral-600)"
            )}
          >
            ×
          </button>
        </div>

        <div style={s("flex:1; min-height:0; overflow:auto; padding:24px 22px")}>
          {error && (
            <div
              style={s(
                "margin-bottom:16px; padding:12px 14px; background:var(--color-accent); color:var(--on-accent); font-family:var(--font-heading); font-weight:800; font-size:13px; letter-spacing:0.08em; text-transform:uppercase"
              )}
            >
              {error}
            </div>
          )}

          <div style={s("margin-bottom:16px")}>
            <div style={fieldLabel}>Title</div>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Retry queue for payment webhooks"
              style={inputStyle}
            />
          </div>

          <div ref={ownerRef} style={s("margin-bottom:16px; position:relative")}>
            <div style={fieldLabel}>Owner</div>
            <button
              type="button"
              onClick={() => setOwnerOpen((v) => !v)}
              style={s(
                "cursor:pointer; width:100%; min-height:46px; padding:8px 14px; display:flex; align-items:center; gap:6px; text-align:left; color:var(--color-text); background:var(--color-surface); border:2px solid var(--color-divider); font:inherit; box-sizing:border-box"
              )}
            >
              <span style={ownerId ? s("font-size:15px") : s("color:var(--color-neutral-600); font-size:15px")}>
                {ownerId ? (users.find((u) => u.id === ownerId)?.name ?? "Select owner") : "Select owner"}
              </span>
              <span style={s("flex:1")} />
              <span style={s("font-size:11px; color:var(--color-neutral-600)")}>{ownerOpen ? "▴" : "▾"}</span>
            </button>

            {ownerOpen && (
              <div
                style={s(
                  "position:absolute; top:100%; left:0; right:0; margin-top:4px; background:var(--color-surface); border:2px solid var(--color-divider); z-index:10; max-height:220px; overflow:auto"
                )}
              >
                {users.length === 0 ? (
                  <div style={s("padding:10px 14px; font-size:14px; color:var(--color-neutral-600)")}>No users</div>
                ) : (
                  users.map((u) => {
                    const active = ownerId === u.id;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setOwnerId(u.id);
                          setOwnerOpen(false);
                        }}
                        style={{
                          ...s(
                            "all:unset; cursor:pointer; display:flex; align-items:center; width:100%; padding:10px 14px; box-sizing:border-box; font-size:14px; color:var(--color-text)"
                          ),
                          background: active ? "var(--color-neutral-200)" : "transparent",
                        }}
                      >
                        {u.name}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div ref={repoRef} style={s("margin-bottom:16px; position:relative")}>
            <div style={fieldLabel}>Repos (optional)</div>
            <button
              type="button"
              onClick={() => setRepoOpen((v) => !v)}
              style={s(
                "cursor:pointer; width:100%; min-height:46px; padding:8px 14px; display:flex; flex-wrap:wrap; align-items:center; gap:6px; text-align:left; color:var(--color-text); background:var(--color-surface); border:2px solid var(--color-divider); font:inherit; box-sizing:border-box"
              )}
            >
              {selectedRepos.length === 0 ? (
                <span style={s("color:var(--color-neutral-600); font-size:15px")}>Select repos</span>
              ) : (
                selectedRepos.map((id) => {
                  const r = repos.find((x) => x.id === id);
                  return (
                    <span
                      key={id}
                      style={s(
                        "display:inline-flex; align-items:center; padding:3px 10px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-200); color:var(--color-neutral-800)"
                      )}
                    >
                      {r?.name || id}
                    </span>
                  );
                })
              )}
              <span style={s("flex:1")} />
              <span style={s("font-size:11px; color:var(--color-neutral-600)")}>{repoOpen ? "▴" : "▾"}</span>
            </button>

            {repoOpen && (
              <div
                style={s(
                  "position:absolute; top:100%; left:0; right:0; margin-top:4px; background:var(--color-surface); border:2px solid var(--color-divider); z-index:10; max-height:220px; overflow:auto"
                )}
              >
                {repos.length === 0 ? (
                  <div style={s("padding:10px 14px; font-size:14px; color:var(--color-neutral-600)")}>No repos</div>
                ) : (
                  repos.map((r) => {
                    const checked = selectedRepos.includes(r.id);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() =>
                          setSelectedRepos((prev) =>
                            checked ? prev.filter((x) => x !== r.id) : [...prev, r.id]
                          )
                        }
                        style={s(
                          "all:unset; cursor:pointer; display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; box-sizing:border-box; font-size:14px; color:var(--color-text)"
                        )}
                      >
                        <span
                          style={{
                            ...s(
                              "flex:none; width:16px; height:16px; border:2px solid var(--color-divider); display:flex; align-items:center; justify-content:center"
                            ),
                            background: checked ? "var(--color-text)" : "transparent",
                          }}
                        >
                          {checked && (
                            <span style={s("display:block; width:8px; height:8px; background:var(--color-bg)")} />
                          )}
                        </span>
                        <span>{r.name}</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div style={s("margin-bottom:16px")}>
            <div style={fieldLabel}>Description (optional)</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What needs doing"
              style={{ ...inputStyle, ...s("min-height:110px; resize:vertical") }}
            />
          </div>

          <div style={s("margin-bottom:4px")}>
            <div style={fieldLabel}>Status</div>
            <div style={s("display:grid; grid-template-columns:1fr 1fr; gap:12px")}>
              {TASK_STATUSES.map((st) => {
                const active = status === st;
                return (
                  <button
                    key={st}
                    type="button"
                     onClick={() => setStatus(st as TaskStatus)}
                    style={{
                      ...s(
                        "all:unset; cursor:pointer; display:flex; align-items:center; justify-content:center; padding:12px 14px; border:2px solid var(--color-divider); font-family:var(--font-heading); font-weight:800; font-size:13px; letter-spacing:0.08em; text-transform:uppercase; text-align:center"
                      ),
                      background: active ? "var(--color-text)" : "transparent",
                      color: active ? "var(--color-bg)" : "var(--color-text)",
                    }}
                  >
                    {st}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div
          style={s(
            "flex:none; display:flex; gap:12px; padding:18px 22px; border-top:2px solid var(--color-divider)"
          )}
        >
          <button
            onClick={submit}
            style={s(
              "all:unset; cursor:pointer; flex:1; display:flex; align-items:center; justify-content:center; padding:14px; background:var(--color-text); color:var(--color-bg); border:2px solid var(--color-text); font-family:var(--font-heading); font-weight:800; font-size:15px; letter-spacing:0.08em; text-transform:uppercase"
            )}
          >
            Create ticket
          </button>
        </div>
      </div>
    </div>
  );
}
